import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {
  AddReactionMutationMutation,
  AddReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/addReactionMutation'
import {useCallback} from 'react'

import {
  completeReactionMutation,
  isLatestReactionMutation,
  type ReactionMutationCallbacks,
  type ReactionMutationContext,
  startReactionMutation,
  submitReactionMutation,
} from './reaction-mutation'

type AddReactionSubject = NonNullable<NonNullable<AddReactionMutationMutation['addReaction']>['subject']>
type AddReactionVariables = AddReactionMutationMutationVariables & ReactionMutationCallbacks<AddReactionSubject>
type AddReactionOperation = SetFieldsOperation & {
  reactionMutation: {variables: AddReactionVariables; token: symbol}
}

export function useAddReactionMutation(_legacyRelayEnvironment?: object) {
  const mutation = useEntityMutation<AddReactionSubject, Error, ReactionMutationContext>({
    mutationFn: async operation => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      const data = await submitReactionMutation<AddReactionMutationMutation>('POST', variables)
      const subject = data.addReaction?.subject
      if (!subject) throw new Error('Add reaction mutation returned no subject')
      return subject
    },
    getConfirmedOperation: (subject, operation) => {
      const {variables, token} = (operation as AddReactionOperation).reactionMutation
      return subject.reactionGroups && isLatestReactionMutation(String(variables.input.subjectId), token)
        ? {type: 'setFields', id: subject.id, fields: {reactionGroups: subject.reactionGroups}}
        : undefined
    },
    onMutate: ({operation}) => {
      const {variables, token} = (operation as AddReactionOperation).reactionMutation
      return {
        mutation: startReactionMutation(String(variables.input.subjectId), token),
      }
    },
    onSuccess: (subject, {operation}, context) => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      if (!context || !completeReactionMutation(String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      variables.onCompleted?.(subject)
    },
    onError: (error, {operation}, context) => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      if (!context || !completeReactionMutation(String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      variables.onError?.(error)
    },
  })

  const mutate = useCallback(
    (variables: AddReactionVariables) => {
      const operation: AddReactionOperation = {
        type: 'setFields',
        id: String(variables.input.subjectId),
        fields: variables.optimisticFields ?? {},
        reactionMutation: {variables, token: Symbol()},
      }
      mutation.mutate({operation})
    },
    [mutation],
  )

  return {...mutation, mutate}
}
